import React, { useEffect, useState } from "react";
import TrashIconSVG from "assets/svg/trash-icon";
import { Container, Draggable, DropResult } from "@edorivai/react-smooth-dnd";
import { RxDragHandleDots2 } from "react-icons/rx";
import UpIconSVG from "assets/svg/up-icon";
import DownIconSVG from "assets/svg/down-icon";
import EditPencilIconSVG from "assets/svg/edit-pencil-icon";
import _ from "lodash";
import CheckIconSVG from "assets/svg/check-icon";
import LoadingIndicator from "assets/svg/loading-indicator";
import { useTranslation } from "react-i18next";

const ImagePreviewWithLoader = ({
  images = [],
  onRemove = () => {},
  progress = [],
  setProgress = () => {},
  errors = {},
  linkTypeIndex,
  linkType,
  setLinkType,
  labelClass = "text-sm font-semibold text-lightBlack",
  maxSelectedImages = 50,
}) => {
  const { t } = useTranslation();
  const [previews, setPreviews] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [renameCache, setRenameCache] = useState({});

  const isUpdating = () => {
    return Object.values(progress).some((p) => p < 100 && p !== undefined);
  }

  const { http } = global.services;

  useEffect(() => {
    setPreviews([]);

    for (let i = 0; i < images.length; i++) {
      if (images[i] && images[i].hasOwnProperty("media")) {
        setPreviews((prev) => [
          ...prev,
          {
            url: images[i].media.url ? images[i].media.url : images[i].url,
            name: images[i].media?.file_name,
            fullFilename: images[i]?.media.file_name,
            rename: images[i].media.rename,
            loading: images[i].media.loading,
            extension: images[i].media.file_extension,
            file_size:
              images[i].media.file_size >= 1000000
                ? Math.ceil(images[i].media.file_size / 1048576) + "MB"
                : Math.ceil(images[i].media.file_size / 1024) + "KB",
            order: i,
          },
        ]);
      } else {
        const fileName = images[i].name;
        const lastDotIndex = fileName.lastIndexOf(".");
        const name = fileName.substring(0, lastDotIndex);
        const extension = fileName.substring(lastDotIndex + 1);
        const reader = new FileReader();

        reader.readAsDataURL(images[i]);

        reader.onload = () => {
          const result = reader.result?.toString() || "";
          if (result) {
            setPreviews((prev) =>
              [
                ...prev,
                {
                  url: result,
                  name: images[i].media?.file_name
                    ? images[i].media.file_name
                    : name,
                  extension: extension,
                  file_size:
                    images[i].size >= 1000000
                      ? Math.ceil(images[i].size / 1048576) + "MB"
                      : Math.ceil(images[i].size / 1024) + "KB",
                  progress: progress[images[i].progressId]
                    ? progress[images[i].progressId]
                    : 1,
                  progressId: images[i].progressId,
                  order: i,
                },
              ].sort((a, b) => a.order - b.order)
            );
          }
        };
      }
    }
  }, [images]);

  useEffect(() => {
    setPreviews((prev) =>
      prev.map((preview) => {
        if (
          preview.progress &&
          preview.progress !== 100 &&
          progress[preview.progressId]
        ) {
          return {
            ...preview,
            progress: progress[preview.progressId],
          };
        } else if (progress[preview.progressId] == 100) {
          const bufferPreview = preview;
          delete bufferPreview.progress;
          return bufferPreview;
        } else {
          return preview;
        }
      })
    );
  }, [images, progress]);

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    // Update the order attribute
    result.forEach((item, index) => {
      item.order = index;
    });
    return result;
  };

  const onDrop = (dropResult) => {
    const { removedIndex, addedIndex } = dropResult;

    const reorderedItems = reorder(
      linkType[linkTypeIndex].value,
      removedIndex,
      addedIndex
    );
    setLinkType((prev) => {
      const newLinkType = [...prev];
      newLinkType[linkTypeIndex].value = reorderedItems;
      return newLinkType;
    });
    // setPreviews(reorderedItems);
  };

  function handleKeyPress(e, index) {
    if (e.key === "Enter") {
      onSaveName(index);
    }
  }


  const onRename = (e, index) => {
    /**
     * NOTE: 
     * instead of using index as cache pointer 
     * use media.id instead to avoid bug in saving
     */
    const mediaId = linkType[linkTypeIndex].value[index].media.id;
    const value = e.target.value;
    setRenameCache((prev) => ({
      ...prev,
      [mediaId]: value,
    }));
  };


  const toggleRename = (index) => {
    /**
     * NOTE: 
     * instead of using index as cache pointer 
     * use media.id instead to avoid bug in saving
     */
    const mediaId =  linkType[linkTypeIndex].value[index].media.id;
    setRenameCache((prev) => ({
      ...prev,
      [mediaId]: linkType[linkTypeIndex].value[index].media.file_name,
    }));
    setLinkType((prev) => {
      const newLinkType = _.cloneDeep(prev);
      newLinkType[linkTypeIndex].value[index].media.rename = true;
      return newLinkType;
    });
  };


  const onSaveName = async (index) => {
    /**
     * NOTE: 
     * instead of using index as cache pointer 
     * use media.id instead to avoid bug in saving
     */
    setProgress((prev) => ({
      ...prev,
      rename: 0,
    }));
    const mediaId =  linkType[linkTypeIndex].value[index].media.id;
    const newName = renameCache[mediaId];
    setLinkType((prev) => {
      const newLinkType = _.cloneDeep(prev);
      newLinkType[linkTypeIndex].value[index].media.file_name = newName;
      newLinkType[linkTypeIndex].value[index].media.loading = true;
      return newLinkType;
    });

    await http.put(`medias/${linkType[linkTypeIndex].value[index].media.id}`, {
      ...linkType[linkTypeIndex].value[index].media,
      file_name: newName,
    });

    setLinkType((prev) => {
      const newLinkType = _.cloneDeep(prev);
      newLinkType[linkTypeIndex].value[index].media.rename = false;
      newLinkType[linkTypeIndex].value[index].media.loading = false;
      return newLinkType;
    });

    // Clean up cache
    setRenameCache((prev) => {
      const copy = { ...prev };
      delete copy[mediaId];
      return copy;
    });
    setProgress((prev) => ({
      ...prev,
      rename: 100,
    }));
  };


  return (
    <div className="w-full flex flex-col">
      {}
      <div className="flex justify-between w-full mb-2">
        <label
          className={`${labelClass} leading-4 ${
            previews.length > maxSelectedImages ? "text-red" : ""
          }`}
        >
          {t("uploadImages")} ({previews.length}/{maxSelectedImages}){" "}
          {previews.length > maxSelectedImages
            ? errors?.multiple_images
              ? `- ${errors.multiple_images[0]}`
              : `- Please remove some images! The maximum number of images that can be uploaded is ${maxSelectedImages}.`
            : ""}
        </label>
        {previews.length > 2 && (
          <div
            className="text-sm font-semibold text-lightBlack cursor-pointer"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? (
              <div>
                Hide Others <UpIconSVG className="ml-2 inline" />
              </div>
            ) : (
              <div className="text-primary">
                Show {previews.length - 2}+ More
                <DownIconSVG className="ml-2 inline" />
              </div>
            )}
          </div>
        )}
      </div>
      <div className={previews.length > 2 && !showAll ? "fade-container" : ""}>
        <Container
          groupName="2"
          dropPlaceholder={false}
          getGhostParent={() => document.querySelector(".smooth-dnd-parent")}
          shouldAnimateDrop={() => false}
          dragClass="tilt"
          // getChildPayload={(i) => previews[i]}
          dragHandleSelector=".dragHandleSelector-preview"
          onDrop={onDrop}
        >
          {(showAll || previews.length <= 2) &&
            previews.map((prev, index) => (
              <Draggable key={index}>
                <div className="flex gap-1 items-center">
                  <span className="text-white rounded-full min-w-5 font-semibold leading-5 bg-black text-xs text-center">
                    {index + 1}
                  </span>
                  <span className={isUpdating() ? "opacity-80 cursor-not-allowed" : "draggable-item dragHandleSelector-preview cursor-move"}>
                    <RxDragHandleDots2 className="text-2xl color-gray dnd-handle" />
                  </span>
                  <div className="flex overflow-hidden pr-2 flex-col md:flex-row items-center justify-center mb-2 rounded-lg border-solid border-gray border w-full bg-white">
                    <img
                      src={prev.url}
                      className="w-14 h-14 mr-4 object-cover"
                      alt="preview"
                    />
                    <div className="flex-1 flex-row items-center">
                      <div className="flex justify-between w-full">
                        {prev.rename ? (
                          <input
                            type="text"
                            className="border-none p-0-important w-full"
                            // value={renameCache[index] ?? prev.fullFilename}
                            /**
                             * NOTE: 
                             * instead of using index as cache pointer 
                             * use media.id instead to avoid bug in saving
                             */
                            value={renameCache[linkType[linkTypeIndex].value[index].media.id] ?? prev.fullFilename}
                            onChange={(e) => onRename(e, index)}
                            onKeyDown={(e) => handleKeyPress(e, index)}
                          />
                        ) : (
                          <p className="text-xs md:text-base text-center md:text-left break-all">{prev.name}</p>
                        )}

                        {prev.progress && (
                          <div
                            className="w-11 flex justify-end"
                            onClick={() => onRemove(index)}
                          >
                            <TrashIconSVG className="cursor-pointer" />
                          </div>
                        )}
                      </div>
                      <div className="flex space-x-2 items-center justify-center md:justify-start">
                        <span className="w-14 text-xs md:text-base text-darkerGray font-semiBold">
                          {prev.file_size}
                        </span>
                        {prev.progress && (
                          <div className="w-full flex items-center">
                            <div className="bg-thBg h-2 rounded-full w-full mr-2">
                              <div
                                className={`h-2 rounded-full ${
                                  prev.progress === 100
                                    ? "bg-lightGreen"
                                    : "bg-primary"
                                }`}
                                style={{ width: `${prev.progress}%` }}
                                id="serverProgress"
                              ></div>
                            </div>
                            <span className="w-11 flex justify-end">
                              {prev.progress}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {!prev.progress && (
                      <div className="flex text-sm">
                        {prev.rename ? (
                          <div
                            onClick={() => onSaveName(index)}
                            className="cursor-pointer text-lightGreen"
                          >
                            {prev.loading ? (
                              <LoadingIndicator className="text-lightGreen w-3 h-3 mr-1 inline" />
                            ) : (
                              <CheckIconSVG className="inline mr-1" />
                            )}
                            Save Name
                          </div>
                        ) : (
                          <>
                            <div
                              className="cursor-pointer"
                              onClick={() => toggleRename(index)}
                            >
                              <EditPencilIconSVG className="inline mr-1" />
                              Rename
                            </div>
                            <div
                              className="w-9 flex justify-end"
                              onClick={() => onRemove(index)}
                            >
                              <TrashIconSVG className="cursor-pointer" />
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Draggable>
            ))}
          {!showAll &&
            previews.length > 2 &&
            previews.slice(0, 2).map((prev, index) => (
              <div key={index}>
                <div className="flex gap-1 items-center">
                  <span className="text-white rounded-full min-w-5 font-semibold leading-5 bg-black text-xs text-center">
                    {index + 1}
                  </span>
                  <span className="draggable-item">
                    <RxDragHandleDots2 className="text-2xl color-gray dnd-handle" />
                  </span>
                  <div className="flex pr-2 flex-col md:flex-row justify-center items-center mb-2 rounded-lg rounded-md border-solid border-gray border w-full bg-white">
                    <img
                      src={prev.url}
                      className="w-14 h-14 mr-4 object-cover"
                      alt="preview"
                    />
                    <div className="flex-1 flex-row items-center">
                      <div className="flex justify-between w-full">
                        {prev.rename ? (
                          <input
                            type="text"
                            className="border-none p-0-important w-full"
                            value={renameCache[linkType[linkTypeIndex].value[index].media.id] ?? prev.fullFilename}
                            onChange={(e) => onRename(e, index)}
                            onKeyDown={(e) => handleKeyPress(e, index)}
                          />
                        ) : (
                          <p className="text-xs md:text-base text-center md:text-left break-all">{prev.name}</p>
                        )}

                        {prev.progress && (
                          <div
                            className="w-11 flex justify-end"
                            onClick={() => onRemove(index)}
                          >
                            <TrashIconSVG className="cursor-pointer" />
                          </div>
                        )}
                      </div>
                      <div className="flex space-x-2 items-center justify-center md:justify-start">
                        <span className="w-14 text-xs md:text-base text-darkerGray font-semiBold">
                          {prev.file_size}
                        </span>
                        {prev.progress && (
                          <div className="w-full flex items-center">
                            <div className="bg-thBg h-2 rounded-full w-full mr-2">
                              <div
                                className={`h-2 rounded-full ${
                                  prev.progress === 100
                                    ? "bg-lightGreen"
                                    : "bg-primary"
                                }`}
                                style={{ width: `${prev.progress}%` }}
                                id="serverProgress"
                              ></div>
                            </div>
                            <span className="w-11 flex justify-end">
                              {prev.progress}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {!prev.progress && (
                      <div className="flex text-sm">
                        {prev.rename ? (
                          <div
                            onClick={() => onSaveName(index)}
                            className="cursor-pointer text-lightGreen"
                          >
                            {prev.loading ? (
                              <LoadingIndicator className="text-lightGreen w-3 h-3 mr-1 inline" />
                            ) : (
                              <CheckIconSVG className="inline mr-1" />
                            )}
                            Save Name
                          </div>
                        ) : (
                          <>
                            <div
                              className="cursor-pointer"
                              onClick={() => toggleRename(index)}
                            >
                              <EditPencilIconSVG className="inline mr-1" />
                              Rename
                            </div>
                            <div
                              className="w-9 flex justify-end"
                              onClick={() => onRemove(index)}
                            >
                              <TrashIconSVG className="cursor-pointer" />
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
        </Container>
      </div>
    </div>
  );
};

export default ImagePreviewWithLoader;
